import { combineReducers } from 'redux';
import { all, put, select, takeLatest, cancelled } from 'redux-saga/effects';
import { getRegion } from 'common/selectors';
import { createAction, createAsyncAction, createReducer } from 'typesafe-actions';
import { api } from 'api';
import { FetchSponsoredProductsProps, FetchSponsoredProductsResponse, SponsoredProductType } from '../types';
import { fetchSponsoredProducts } from './api';

export const FETCH_SPONSORED_PRODUCTS = createAsyncAction(
  'FETCH_SPONSORED_PRODUCTS/request',
  'FETCH_SPONSORED_PRODUCTS/success',
  'FETCH_SPONSORED_PRODUCTS/failure',
  'FETCH_SPONSORED_PRODUCTS/cancel',
)<Omit<FetchSponsoredProductsProps, 'regionCode'>, FetchSponsoredProductsResponse, boolean, void>();

export const RESET = createAction('FETCH_SPONSORED_PRODUCTS/RESET')();

export const SET_FALLBACK = createAction('FETCH_SPONSORED_PRODUCTS/SET_FALLBACK')();

export const data = createReducer<SponsoredProductType[] | null>(null)
  .handleAction(FETCH_SPONSORED_PRODUCTS.success, (_, { payload }) => payload.content ?? [])
  .handleAction([FETCH_SPONSORED_PRODUCTS.request, FETCH_SPONSORED_PRODUCTS.failure, RESET], () => null);

export const isFallback = createReducer<boolean | null>(null)
  .handleAction(FETCH_SPONSORED_PRODUCTS.success, (_, { payload }) => payload.isFallback ?? null)
  .handleAction(SET_FALLBACK, () => true);

export const isFetching = createReducer(false)
  .handleAction(FETCH_SPONSORED_PRODUCTS.request, () => true)
  .handleAction(
    [
      FETCH_SPONSORED_PRODUCTS.success,
      FETCH_SPONSORED_PRODUCTS.failure,
      FETCH_SPONSORED_PRODUCTS.cancel,
      RESET,
    ],
    () => false,
  );

export const error = createReducer<boolean>(false)
  .handleAction([FETCH_SPONSORED_PRODUCTS.failure], (_, { payload }) => payload)
  .handleAction([FETCH_SPONSORED_PRODUCTS.request, FETCH_SPONSORED_PRODUCTS.success, RESET], () => false);

export const sponsoredProducts = combineReducers({
  data,
  isFetching,
  error,
});

export function* runSponsoredProductsRefetch(
  props: Omit<FetchSponsoredProductsProps, 'regionCode'>,
): Generator {
  const abortController = api.abortController();
  const regionCode = (yield select(getRegion)) as ReturnType<typeof getRegion>;

  try {
    const response = yield fetchSponsoredProducts({ regionCode, ...props }, abortController.token);

    yield put(FETCH_SPONSORED_PRODUCTS.success(response as FetchSponsoredProductsResponse));
  } catch {
    yield put(FETCH_SPONSORED_PRODUCTS.failure(true));
  } finally {
    if (yield cancelled()) {
      abortController.cancel();
      yield put(FETCH_SPONSORED_PRODUCTS.cancel());
    }
  }
}

export function* rootSaga() {
  yield all([
    takeLatest(FETCH_SPONSORED_PRODUCTS.request, ({ payload }) => runSponsoredProductsRefetch(payload)),
  ]);
}
